// PARITY-0 (task detail assembler, web half; hybrid design §3.1, §3.3): the task's blocks and
// attention cards as registries. Wave-1 packages register what they own (P1 description,
// subtasks, files, metrics; P2 the conversation and agent cards; P4 mirrors; P5 the playbook)
// instead of editing the drawer, and the same blocks render in the three hosts (drawer, full
// page, inbox preview). A block's `id` is what the board's task layout places (`description`,
// `field:<id>`, …; design §3.3); placement `body` is the work pane, `props` the properties
// column. Lists read sorted by order, then id; a taken id or kind throws.
import type { ComponentType } from "react";
import type { Api } from "../../../api/client";
import { orderedRegistry } from "../../../extensions/ordered-registry";
import type { CompanyAccess } from "../../company";
import type { Board, TaskDetail } from "../view-engine";

/** Where the task renders (design §3.1–3.2): the drawer, the full page, the Inbox preview. */
export type TaskHost = "drawer" | "page" | "inbox";

/** What the viewer may do on the task: `edit` it, `manage` its board (layout, settings). */
export type TaskCan = { edit: boolean; manage: boolean };

export type TaskBlockProps = {
  /** P1 wave 1: the API and the company, so a block reads and writes on its own. */
  api: Api;
  company: CompanyAccess;
  task: TaskDetail;
  board: Board;
  host: TaskHost;
  can: TaskCan;
  /** Opens another task in the same host (a subtask, a dependency, a backlink). */
  onOpenTask: (taskId: string) => void;
};

export type TaskBlock = {
  id: string;
  title: string;
  placement: "body" | "props";
  /** Lower first; ties by id. */
  order: number;
  /** An empty block renders as its one "+ Add …" row (design §3.5), never hidden. */
  isEmpty: (task: TaskDetail) => boolean;
  Component: ComponentType<TaskBlockProps>;
};

/** One item of the attention strip (an approval, a question, a run result: design §3.1). */
export type TaskCardItem = { kind: string; id: string; [key: string]: unknown };

export type TaskCardProps = { item: TaskCardItem; can: TaskCan };

export type TaskCard = {
  /** The item kind this card renders (`approval`, `question`, `run_result`, …). */
  kind: string;
  /** Lower first in the strip; ties by kind. */
  order: number;
  Component: ComponentType<TaskCardProps>;
};

const blocks = orderedRegistry<TaskBlock>("task block");
const cards = orderedRegistry<TaskCard & { id: string }>("task card");

/** Adds a block; throws when its id is taken. Returns the unregister function. */
export function registerTaskBlock(block: TaskBlock): () => void {
  return blocks.register(block);
}

/** Every registered block, sorted by order, then id. */
export function taskBlocks(): TaskBlock[] {
  return blocks.list();
}

/** The blocks of one placement (the work pane's, or the properties column's). */
export function taskBlocksIn(placement: TaskBlock["placement"]): TaskBlock[] {
  return blocks.list().filter((b) => b.placement === placement);
}

/** The block a layout names (`undefined`: not registered in this build; the layout skips it). */
export function taskBlock(id: string): TaskBlock | undefined {
  return blocks.list().find((b) => b.id === id);
}

/** Adds the card of an item kind; throws when the kind has one. Returns the unregister function. */
export function registerTaskCard(card: TaskCard): () => void {
  return cards.register({ ...card, id: card.kind });
}

/** Every registered card, sorted by order, then kind. */
export function taskCards(): TaskCard[] {
  return cards.list().map(({ id: _id, ...c }) => c);
}

/** The card for an item (`undefined`: no card for its kind; the strip leaves the item out). */
export function taskCardFor(item: TaskCardItem): TaskCard | undefined {
  return taskCards().find((c) => c.kind === item.kind);
}
